import { useEffect, useMemo, useState } from "react";
import { computeCityPrices, findCity, GRAMS_PER_TOLA } from "@/lib/gold-data";
import { computeSilverPrices } from "@/lib/silver-data";
import { useGoldRates, useSilverRates } from "@/lib/nonblocking-rates";
import { CalculatorActions, readCalculatorQuery } from "@/components/calculator/CalculatorActions";

const MAX = 10_000_000_000;
const NISAB_GOLD_G = 87.48;
const NISAB_SILVER_G = 612.36;

type Field = { id: string; label: string; hint: string; group: "metal" | "asset" | "liability" };

const FIELDS: Field[] = [
  { id: "gold", label: "Gold weight", hint: "Jewellery, coins and bars combined", group: "metal" },
  { id: "silver", label: "Silver weight", hint: "Jewellery, utensils and bars", group: "metal" },
  { id: "cash", label: "Cash at home", hint: "PKR and foreign cash converted to PKR", group: "asset" },
  { id: "bank", label: "Bank & wallet balances", hint: "Savings, current, JazzCash / Easypaisa", group: "asset" },
  { id: "invest", label: "Shares, funds & savings certificates", hint: "Current market value", group: "asset" },
  { id: "bonds", label: "Prize bonds", hint: "Face value of all bonds", group: "asset" },
  { id: "business", label: "Business stock for sale", hint: "Stock value at current sale price", group: "asset" },
  { id: "owed", label: "Money others owe you", hint: "Only loans you expect to recover", group: "asset" },
  { id: "debts", label: "Debts due now", hint: "Loans, instalments due this month", group: "liability" },
  { id: "bills", label: "Unpaid bills & rent", hint: "Utility bills, rent, wages owed", group: "liability" },
];

const KEYS = [...FIELDS.map((f) => f.id), "karat", "unit", "basis"];

function num(v: string | undefined): number {
  const n = parseFloat(String(v ?? "").trim());
  return Number.isFinite(n) ? Math.min(MAX, Math.max(0, n)) : 0;
}

function rs(value: number): string {
  return `Rs ${Math.round(Number.isFinite(value) ? value : 0).toLocaleString("en-PK")}`;
}

const EMPTY: Record<string, string> = { karat: "22", unit: "tola", basis: "silver" };

export function ZakatCalculator() {
  const { data: gold } = useGoldRates();
  const { data: silver } = useSilverRates();
  const karachi = findCity("pakistan", "karachi")!;
  const goldPrices = computeCityPrices({
    spotUsdPerOz: gold?.spotUsdPerOz ?? 0,
    fxRate: gold?.pkrPerUsd ?? 0,
    country: "pakistan",
    cityPremium: karachi.premium,
  });
  const silverPrices = computeSilverPrices({
    spotUsdPerOz: silver?.spotUsdPerOz ?? 0,
    fxRate: silver?.pkrPerUsd ?? 0,
    country: "pakistan",
  });
  const gold24PerGram = Number.isFinite(goldPrices.perGram24) ? goldPrices.perGram24 : 0;
  const silverPerTola = Number.isFinite(silverPrices.perTola) ? silverPrices.perTola : 0;
  const silverPerGram = silverPerTola / GRAMS_PER_TOLA;

  const [v, setV] = useState<Record<string, string>>(EMPTY);
  useEffect(() => {
    const q = readCalculatorQuery("zakat", KEYS);
    if (Object.keys(q).length) setV((prev) => ({ ...prev, ...q }));
  }, []);
  const set = (id: string, value: string) => setV((prev) => ({ ...prev, [id]: value }));

  const r = useMemo(() => {
    const toGrams = (x: number) => (v.unit === "gram" ? x : x * GRAMS_PER_TOLA);
    const karat = [24, 22, 21, 18].includes(Number(v.karat)) ? Number(v.karat) : 22;
    const goldG = toGrams(num(v.gold));
    const silverG = toGrams(num(v.silver));
    const goldValue = goldG * gold24PerGram * (karat / 24);
    const silverValue = silverG * silverPerGram;
    const other = FIELDS.filter((f) => f.group === "asset").reduce((s, f) => s + num(v[f.id]), 0);
    const assets = goldValue + silverValue + other;
    const liabilities = num(v.debts) + num(v.bills);
    const net = Math.max(0, assets - liabilities);
    const nisabSilver = NISAB_SILVER_G * silverPerGram;
    const nisabGold = NISAB_GOLD_G * gold24PerGram;
    const nisab = v.basis === "gold" ? nisabGold : nisabSilver;
    const due = nisab > 0 && net >= nisab;
    return { karat, goldValue, silverValue, other, assets, liabilities, net, nisab, nisabGold, nisabSilver, due, zakat: due ? net * 0.025 : 0 };
  }, [v, gold24PerGram, silverPerGram]);

  const ready = gold24PerGram > 0 && silverPerGram > 0;
  const resultText = `Zakat calculation (DrHint)\nTotal zakatable assets: ${rs(r.assets)}\nLiabilities deducted: ${rs(r.liabilities)}\nNet wealth: ${rs(r.net)}\nNisab (${v.basis}): ${rs(r.nisab)}\nZakat payable (2.5%): ${rs(r.zakat)}`;

  const input = "w-full h-11 rounded-md border border-input bg-background px-3 text-base shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

  const renderField = (f: Field) => (
    <label key={f.id} className="block">
      <span className="block text-sm font-medium">
        {f.label}
        {f.group === "metal" ? ` (${v.unit === "gram" ? "grams" : "tola"})` : " (PKR)"}
      </span>
      <input
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        placeholder="0"
        value={v[f.id] ?? ""}
        onChange={(e) => set(f.id, e.target.value)}
        className={`${input} mt-1.5`}
      />
      <span className="mt-1 block text-[11px] text-muted-foreground">{f.hint}</span>
    </label>
  );

  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="space-y-6 rounded-2xl border border-border bg-card p-5 shadow-sm md:p-7 lg:basis-3/5">
          <div>
            <h2 className="text-xl font-bold tracking-tight md:text-2xl">1. Gold & silver</h2>
            <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="block text-sm font-medium">Weight unit</span>
                <select value={v.unit} onChange={(e) => set("unit", e.target.value)} className={`${input} mt-1.5`}>
                  <option value="tola">Tola (11.664 g)</option>
                  <option value="gram">Gram</option>
                </select>
              </label>
              <label className="block">
                <span className="block text-sm font-medium">Gold karat</span>
                <select value={v.karat} onChange={(e) => set("karat", e.target.value)} className={`${input} mt-1.5`}>
                  <option value="24">24K (99.9%)</option>
                  <option value="22">22K (91.6%)</option>
                  <option value="21">21K (87.5%)</option>
                  <option value="18">18K (75%)</option>
                </select>
              </label>
              {FIELDS.filter((f) => f.group === "metal").map(renderField)}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-bold tracking-tight md:text-2xl">2. Cash, savings & business</h2>
            <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {FIELDS.filter((f) => f.group === "asset").map(renderField)}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-bold tracking-tight md:text-2xl">3. Deduct what you owe</h2>
            <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {FIELDS.filter((f) => f.group === "liability").map(renderField)}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-bold tracking-tight md:text-2xl">4. Nisab standard</h2>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Nisab standard">
              {(["silver", "gold"] as const).map((b) => (
                <button
                  key={b}
                  type="button"
                  role="radio"
                  aria-checked={v.basis === b}
                  onClick={() => set("basis", b)}
                  className={`min-h-11 rounded-lg border p-3 text-left text-sm ${v.basis === b ? "border-primary bg-primary/10" : "border-border"}`}
                >
                  <span className="block font-semibold">{b === "silver" ? "Silver nisab (612.36 g)" : "Gold nisab (87.48 g)"}</span>
                  <span className="text-xs text-muted-foreground">
                    Today ≈ {rs(b === "silver" ? r.nisabSilver : r.nisabGold)}
                    {b === "silver" ? " · commonly used in Pakistan" : ""}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:basis-2/5">
          <div className="min-h-[420px] rounded-2xl border border-primary/30 bg-primary/10 p-5 shadow-md md:p-7 lg:sticky lg:top-20">
            <h2 className="text-xl font-bold tracking-tight md:text-2xl">Zakat summary</h2>
            <dl className="mt-5 space-y-2.5 text-sm">
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Gold ({r.karat}K)</dt><dd className="font-medium tabular-nums">{rs(r.goldValue)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Silver</dt><dd className="font-medium tabular-nums">{rs(r.silverValue)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Cash, savings & business</dt><dd className="font-medium tabular-nums">{rs(r.other)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Less: debts & bills</dt><dd className="font-medium tabular-nums">− {rs(r.liabilities)}</dd></div>
              <div className="flex justify-between gap-3 border-t border-border pt-2 font-semibold"><dt>Net zakatable wealth</dt><dd className="tabular-nums">{rs(r.net)}</dd></div>
            </dl>

            <div className="mt-4 rounded-xl border border-border bg-background/70 p-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Nisab ({v.basis})</span><span className="tabular-nums">{rs(r.nisab)}</span></div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
                <div className="h-full bg-primary transition-all" style={{ width: `${r.nisab > 0 ? Math.min(100, (r.net / r.nisab) * 100) : 0}%` }} />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {!ready
                  ? "Loading today's gold and silver rates…"
                  : r.due
                    ? "Your net wealth is above nisab — zakat is due if you have held it for one lunar year."
                    : `Below nisab by ${rs(Math.max(0, r.nisab - r.net))} — zakat is not obligatory on this amount.`}
              </p>
            </div>

            <div className="mt-3 rounded-xl bg-primary p-4 text-primary-foreground shadow-sm">
              <div className="text-xs uppercase tracking-wide opacity-80">Zakat payable (2.5%)</div>
              <div className="mt-1 text-3xl font-black tabular-nums">{rs(r.zakat)}</div>
            </div>

            <div className="mt-4">
              <CalculatorActions values={v} resultText={resultText} onReset={() => setV(EMPTY)} queryPrefix="zakat" />
            </div>

            <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
              Rates: 24K gold {rs(gold24PerGram * GRAMS_PER_TOLA)}/tola, silver {rs(silverPerTola)}/tola (Karachi reference). This is a
              calculation aid, not a fatwa — ask a scholar about jewellery in daily use, long-term loans and business specifics.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
